import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/apiService';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Globe,
  FileText,
  Languages,
  BarChart3,
  Plus,
  BookOpen,
  Database,
  BookOpenCheck,
  MessageSquare,
  AlertCircle,
  RefreshCcw
} from 'lucide-react';

interface DashboardStats {
  totalSites: number;
  totalTranslations: number;
  totalLanguages: number;
  completionPercentage: number;
  recentActivity: Activity[];
  topSites: SiteStats[];
  languageDistribution: LanguageStats[];
}

interface Activity {
  id: string;
  type: string;
  message: string;
  timestamp: string;
  siteName?: string;
}

interface SiteStats {
  id: string;
  name: string;
  domain: string;
  translations: number;
  completion: number;
}

interface LanguageStats {
  code: string;
  name: string;
  count: number;
  percentage: number;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { sites } = await apiService.getSites();
      const languages = await apiService.getLanguages();

      const totalSites = sites.length;
      const totalTranslations = sites.reduce((sum, site) => sum + (site.total_keys || 0), 0);
      const avgCompletion = sites.length > 0
        ? sites.reduce((sum, site) => sum + (site.translated_percentage || 0), 0) / sites.length
        : 0;

      const topSites: SiteStats[] = sites
        .map(site => ({
          id: site.id,
          name: site.name,
          domain: site.domain,
          translations: site.total_keys || 0,
          completion: site.translated_percentage || 0
        }))
        .sort((a, b) => b.translations - a.translations)
        .slice(0, 5);

      const languageCounts: Record<string, number> = {};
      sites.forEach(site => {
        site.enabled_languages.forEach(lang => {
          languageCounts[lang] = (languageCounts[lang] || 0) + 1;
        });
      });

      const languageDistribution: LanguageStats[] = Object.entries(languageCounts)
        .map(([code, count]) => {
          const lang = languages.find(l => l.code === code);
          return {
            code,
            name: lang?.name || code,
            count,
            percentage: sites.length > 0 ? (count / sites.length) * 100 : 0
          };
        })
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      const recentActivity: Activity[] = sites.slice(0, 5).map((site, index) => ({
        id: `activity-${index}`,
        type: index % 2 === 0 ? 'translation' : 'site',
        message: index % 2 === 0
          ? `New translation added to ${site.name}`
          : `Site ${site.name} updated`,
        timestamp: new Date(Date.now() - index * 3600000).toISOString(),
        siteName: site.name
      }));

      setStats({
        totalSites,
        totalTranslations,
        totalLanguages: languages.length,
        completionPercentage: Math.round(avgCompletion),
        recentActivity,
        topSites,
        languageDistribution
      });
    } catch (err: any) {
      console.error('Failed to load dashboard:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'translation': return <Globe className="h-5 w-5 text-primary" />;
      case 'site': return <FileText className="h-5 w-5 text-primary" />;
      case 'language': return <Languages className="h-5 w-5 text-primary" />;
      default: return <FileText className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getTimeAgo = (timestamp: string) => {
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Skeleton className="h-9 w-48 mb-2" />
          <Skeleton className="h-5 w-96" />
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-12 w-12 rounded-lg mb-4" />
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16" />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          <div>
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-10" />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="h-6 w-6 text-destructive flex-shrink-0" />
              <div>
                <h3 className="font-medium text-destructive">Error loading dashboard</h3>
                <p className="mt-1 text-sm text-destructive/80">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadDashboardData}
                  className="mt-3"
                >
                  <RefreshCcw className="h-4 w-4 mr-2" />
                  Try again
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-muted-foreground">No data available</p>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Welcome back! Here's an overview of your localization platform.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {/* Total Sites */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Globe className="h-6 w-6 text-primary" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">Total Sites</p>
                <p className="text-2xl font-semibold text-foreground">{stats.totalSites}</p>
              </div>
            </div>
            <div className="mt-4">
              <Link to="/sites" className="text-sm font-medium text-primary hover:underline">
                View all sites
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Total Translations */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">Total Translations</p>
                <p className="text-2xl font-semibold text-foreground">
                  {stats.totalTranslations.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="mt-4">
              <Link to="/translations" className="text-sm font-medium text-primary hover:underline">
                View translations
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Languages */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Languages className="h-6 w-6 text-primary" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">Languages Available</p>
                <p className="text-2xl font-semibold text-foreground">{stats.totalLanguages}</p>
              </div>
            </div>
            <div className="mt-4">
              <Link to="/languages" className="text-sm font-medium text-primary hover:underline">
                Manage languages
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Completion Rate */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-primary" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">Avg Completion</p>
                <p className="text-2xl font-semibold text-foreground">{stats.completionPercentage}%</p>
              </div>
            </div>
            <div className="mt-3">
              <div className="w-full bg-secondary rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${stats.completionPercentage}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Top Sites & Recent Activity */}
        <div className="lg:col-span-2 space-y-8">
          {/* Top Sites */}
          <Card>
            <CardHeader>
              <CardTitle>Top Sites by Translations</CardTitle>
            </CardHeader>
            <CardContent>
              {stats.topSites.length === 0 ? (
                <div className="text-center py-8">
                  <Globe className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No sites yet</p>
                  <Link to="/sites" className="mt-2 inline-block text-primary hover:underline">
                    Create your first site
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {stats.topSites.map((site, index) => (
                    <div key={site.id} className="flex items-center justify-between">
                      <div className="flex items-center flex-1">
                        <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-semibold text-sm">
                          {index + 1}
                        </div>
                        <div className="ml-4 flex-1">
                          <Link
                            to={`/sites/${site.id}/translations`}
                            className="text-sm font-medium text-foreground hover:text-primary"
                          >
                            {site.name}
                          </Link>
                          <p className="text-xs text-muted-foreground">{site.domain}</p>
                        </div>
                      </div>
                      <div className="ml-4 flex-shrink-0 text-right">
                        <p className="text-sm font-semibold text-foreground">
                          {site.translations.toLocaleString()}
                        </p>
                        <p className="text-xs text-muted-foreground">{site.completion}% complete</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              {stats.recentActivity.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No recent activity
                </div>
              ) : (
                <div className="flow-root">
                  <ul className="-mb-8">
                    {stats.recentActivity.map((activity, index) => (
                      <li key={activity.id}>
                        <div className="relative pb-8">
                          {index !== stats.recentActivity.length - 1 && (
                            <span
                              className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-border"
                              aria-hidden="true"
                            />
                          )}
                          <div className="relative flex items-start space-x-3">
                            <div className="relative">
                              <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                                {getActivityIcon(activity.type)}
                              </div>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-foreground">{activity.message}</p>
                              <p className="mt-0.5 text-xs text-muted-foreground">
                                {getTimeAgo(activity.timestamp)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Quick Actions & Language Distribution */}
        <div className="space-y-8">
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button asChild className="w-full">
                <Link to="/sites">
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Site
                </Link>
              </Button>
              <Button asChild variant="secondary" className="w-full">
                <Link to="/translations">
                  <FileText className="h-4 w-4 mr-2" />
                  Manage Translations
                </Link>
              </Button>
              <Button asChild variant="secondary" className="w-full">
                <Link to="/glossaries">
                  <BookOpen className="h-4 w-4 mr-2" />
                  View Glossaries
                </Link>
              </Button>
              <Button asChild variant="secondary" className="w-full">
                <Link to="/translation-memory">
                  <Database className="h-4 w-4 mr-2" />
                  Translation Memory
                </Link>
              </Button>
            </CardContent>
          </Card>

          {/* Language Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Popular Languages</CardTitle>
            </CardHeader>
            <CardContent>
              {stats.languageDistribution.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  No languages configured
                </div>
              ) : (
                <div className="space-y-3">
                  {stats.languageDistribution.map((lang) => (
                    <div key={lang.code}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-medium text-foreground">{lang.name}</span>
                        <span className="text-muted-foreground">
                          {lang.count} {lang.count === 1 ? 'site' : 'sites'}
                        </span>
                      </div>
                      <div className="w-full bg-secondary rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all duration-300"
                          style={{ width: `${lang.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Help & Resources */}
          <Card className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground">
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold mb-2">Need Help?</h3>
              <p className="text-sm text-primary-foreground/80 mb-4">
                Check out our documentation and guides to get the most out of LocalizationHub.
              </p>
              <div className="space-y-2">
                <a
                  href="/docs"
                  className="flex items-center text-sm hover:text-primary-foreground/80 transition"
                >
                  <BookOpenCheck className="h-4 w-4 mr-2" />
                  Documentation
                </a>
                <a
                  href="/docs/api"
                  className="flex items-center text-sm hover:text-primary-foreground/80 transition"
                >
                  <Database className="h-4 w-4 mr-2" />
                  API Reference
                </a>
                <a
                  href="/support"
                  className="flex items-center text-sm hover:text-primary-foreground/80 transition"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Get Support
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
